import * as React from "react";
import { useLocalization } from "@shared-ui/localization-context";
import { Viewport, ViewSmall, ViewMedium } from "@shared-ui/viewport-context";

import { UitkCard, UitkCardContentSection } from "uitk-react-cards";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { UitkHeading, UitkText } from "uitk-react-text";
import { UitkLayoutFlex, UitkLayoutFlexItem } from "uitk-react-layout-flex";
import { UitkSpacing } from "uitk-react-spacing";

import LocalizedReviewTitleAndRating from "components/flexComponents/HotelRecentReviews/components/ReviewTitleAndRating";
import { LocalizedReviewAuthorAndPostedDate } from "components/flexComponents/HotelRecentReviews/components/ReviewAuthorAndPostedDate";
import { LocalizedReviewCardLink } from "components/flexComponents/HotelRecentReviews/components/ReviewLink";
import IsomorphicCarousel from "src/components/shared/IsomorphicCarousel/IsomorphicCarousel";
import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";
import { ItemKeyHelper } from "components/utility/ItemKeyHelper";
import { withStores } from "src/stores";

import {
  HotelRecentReviewsFlexModuleResult,
  HotelReview,
} from "typings/microserviceModels/hotel-recent-reviews-flex-module";
import { HotelRecentReviewsProps } from "./typings";
import { observer } from "mobx-react";
import { LazyLoad } from "components/utility/LazyLoad/LazyLoad";
import { getFmId } from "src/stores/ExperienceTemplateStore";

export const HotelRecentReviews = withStores(
  "analytics",
  "context",
  "flexModuleModelStore"
)(
  observer((props: HotelRecentReviewsProps) => {
    const { templateComponent, flexModuleModelStore, context } = props;

    if (!templateComponent) {
      return null;
    }

    const {
      metadata: { id },
      config,
    } = templateComponent;
    const { fmTitleId } = config;
    const fmId = getFmId(templateComponent);
    const model = flexModuleModelStore.getModel(id) as HotelRecentReviewsFlexModuleResult | null;
    const keyHelper = new ItemKeyHelper("hotel-recent-reviews");

    if (!model || !model.hotelReviewList || model.hotelReviewList.length === 0) {
      return null;
    }

    const { title, hotelReviewList } = model;
    const { formatText } = useLocalization();

    return (
      <LazyLoad context={context}>
        <div
          className="HotelRecentReviews all-t-padding-three all-b-padding-three all-cell-1-1 uitk-cell"
          id={id}
          data-fm={fmId}
          data-fm-title-id={fmTitleId}
        >
          <div className="carouselMediumContainer">
            <UitkHeading tag="h2" size={4}>
              {title}
            </UitkHeading>
            <UitkSpacing margin={{ blockstart: "two" }}>
              <IsomorphicCarousel
                itemsVisible={{ sm: 1, md: 1, lg: 2 }}
                pageBy={1}
                peekWidth={11}
                buttonText={{
                  nextButton: formatText("carousel.item.next"),
                  prevButton: formatText("carousel.item.prev"),
                }}
              >
                {hotelReviewList.map((hotel, hotelIndex) => (
                  <HotelRecentReviewCarouselCard
                    key={keyHelper.next()}
                    hotelRecentReview={hotel}
                    index={hotelIndex}
                    showReviewSuperlative={model.showReviewSuperlative}
                  />
                ))}
              </IsomorphicCarousel>
            </UitkSpacing>
          </div>
        </div>
      </LazyLoad>
    );
  })
);

const ReviewCardContent = ({
  hotelReview,
  index,
  showReviewSuperlative,
}: {
  hotelReview: HotelReview;
  index: number;
  showReviewSuperlative: boolean;
}) => (
  <UitkCardContentSection>
    <LocalizedReviewTitleAndRating
      rating={hotelReview.rating}
      reviewTitle={hotelReview.reviewTitle}
      showReviewSuperlative={showReviewSuperlative}
    />
    <ReviewText reviewText={hotelReview.reviewText} />
    <LocalizedReviewAuthorAndPostedDate
      authorName={hotelReview.authorName}
      authorLocation={hotelReview.authorLocation}
      datePosted={hotelReview.date}
      hotelName={hotelReview.hotelName}
      index={index}
    />
    <LocalizedReviewCardLink
      index={index}
      hotelName={hotelReview.hotelName}
      hotelInfositeUrl={hotelReview.hotelInfositeUrl}
    />
  </UitkCardContentSection>
);

const ReviewText = ({ reviewText }: { reviewText: string }) => (
  <UitkSpacing margin={{ block: "one" }}>
    <UitkText overflow="truncate-2-lines" size={300}>
      {reviewText}
    </UitkText>
  </UitkSpacing>
);

const HotelRecentReviewCarouselCard = ({
  hotelRecentReview,
  index,
  showReviewSuperlative,
}: {
  hotelRecentReview: HotelReview;
  index: number;
  showReviewSuperlative: boolean;
}) => {
  return (
    <Viewport>
      <ViewSmall>
        <UitkCard className="HotelRecentReviewsCard" border>
          <UitkFigure ratio={UitkFigureAspectRatioType.R16_9}>
            <LazyLoadedImage src={hotelRecentReview.hotelImageUrl} alt={hotelRecentReview.hotelName} />
          </UitkFigure>
          <ReviewCardContent
            hotelReview={hotelRecentReview}
            index={index + 1}
            showReviewSuperlative={showReviewSuperlative}
          />
        </UitkCard>
      </ViewSmall>

      <ViewMedium>
        <UitkCard className="HotelRecentReviewsCard">
          <UitkLayoutFlex>
            <UitkLayoutFlexItem minWidth={160} shrink={0}>
              <div>
                <UitkFigure ratio={UitkFigureAspectRatioType.R1_1} className="all-image-fit-cover">
                  <LazyLoadedImage src={hotelRecentReview.hotelImageUrl} alt={hotelRecentReview.hotelName} />
                </UitkFigure>
              </div>
            </UitkLayoutFlexItem>
            <UitkLayoutFlexItem grow={1}>
              <ReviewCardContent
                hotelReview={hotelRecentReview}
                index={index}
                showReviewSuperlative={showReviewSuperlative}
              />
            </UitkLayoutFlexItem>
          </UitkLayoutFlex>
        </UitkCard>
      </ViewMedium>
    </Viewport>
  );
};

export default HotelRecentReviews;
